import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { capitalize } from "lodash";
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  MenuItem,
  Typography,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";

import { FilledTextField, FilledSelect } from "../../components/FormElements";
import { passesFleetSettingsSchema } from "../../validation/schemas";
import { FleetContext, FleetSettingsContext } from "../../context";
import { ConflictError } from "../../services/errors.service";
import { PassOfferConstants } from "../../constants";

const NewPassOffer = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { activeFleet } = useContext(FleetContext);
  const {
    passes,
    passOfferTypes,
    activeRatePlan,
    setPassesSettingsForm,
    fetchRatePlan,
    fetchPassOfferTypes,
    createNewPassOffer,
    validateForm,
    invalids,
    refreshPassOffers,
  } = useContext(FleetSettingsContext);
  const history = useHistory();
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [dirtyFields, setDirtyFields] = useState([]);

  const handleFormChange = (key, val) => {
    const updatedForm = { ...passes, [key]: val };
    if (
      key === PassOfferConstants.PASS_OFFER_TYPE_ID &&
      passOfferTypes.find(
        (el) =>
          el.id === updatedForm.pass_offer_type_id && el.offer_type === PassOfferConstants.LINKUP,
      )
    ) {
      updatedForm.price = 0;
    }
    setPassesSettingsForm(updatedForm);
  };

  const findValidationError = (field) => invalids.passes.find((err) => err.includes(field));

  const handleClose = (e = null) => {
    if (e) e.stopPropagation();
    setOpen(false);
    setPassesSettingsForm(null);
    history.goBack();
  };

  const handleSubmit = async () => {
    if (!activeFleet.uuid4 || !activeRatePlan.currency) return;
    setLoading(true);
    const data = passesFleetSettingsSchema.cast(passes, {
      stripUnknown: true,
    });
    data.start_date = data.start_date.toISOString().replace(/T.*/, "T00:00:00.000Z");
    data.end_date = data.end_date.toISOString().replace(/T.*/, "T23:59:59.000Z");
    data.fleet = activeFleet.uuid4;
    data.visible = true;
    data.currency = activeRatePlan.currency;
    try {
      await createNewPassOffer(data);
      await refreshPassOffers(activeFleet);
      setSubmitError(null);
      handleClose();
    } catch (err) {
      if (err instanceof ConflictError)
        setSubmitError(t("Fleet.Settings.NewPassModal.errorMessage"));

      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleFetchPassOfferTypes = async () => {
      await fetchPassOfferTypes();
    };
    handleFetchPassOfferTypes();
  }, []);

  useEffect(() => {
    if (activeRatePlan?.currency)
      setPassesSettingsForm({ ...passes, currency: activeRatePlan.currency });
  }, [activeRatePlan]);

  const handleFetchRatePlan = async () => {
    setLoading(true);
    await fetchRatePlan(activeFleet?.rate_plan_id);
    setLoading(false);
  };

  const passOfferTypeStrParser = (type) => {
    const offerType = type.offer_type;

    const singularOrPlural = type.offer_type === PassOfferConstants.DAILY ? "day" : "days";
    const dayOrDays = t(`Fleet.Settings.Passes.OfferTypes.${singularOrPlural}`);
    const offerLength = `${type.length} ${dayOrDays}`;

    const tripCount = `${type.max_rides === null ? "unlimited" : type.max_rides}`;
    const trips = t("Fleet.Settings.Passes.OfferTypes.trips");
    const offerMaxRides = `${tripCount} ${trips}`;

    const mins = t("Fleet.Settings.Passes.OfferTypes.minutes");
    const offerMinuteLimit = `${type.free_ride_time_limit} ${mins}`;

    return `${capitalize(offerType)} - ${offerLength} - ${offerMaxRides} - ${offerMinuteLimit}`;
  };

  useEffect(() => {
    if (!activeRatePlan && activeFleet) {
      handleFetchRatePlan(activeFleet.rate_plan_id);
    }
  }, [activeFleet?.uuid4]);

  useEffect(() => {
    // debounce validation
    const timeout = setTimeout(async () => {
      await validateForm("passes");
    }, 750);
    return () => clearTimeout(timeout);
  }, [passes.pass_offer_type_id, passes.start_date, passes.end_date, passes.price]);

  const getStandardTextFieldProps = (field) => ({
    inputProps: {
      "data-testid": field,
    },
    value: passes?.[field],
    label: t(`Fleet.Settings.NewPassModal.${field}`),
    onChange: (e) => handleFormChange(field, e.target.value),
    onBlur: () => setDirtyFields([...dirtyFields, field]),
    underlineColor: theme.palette.text.lightgrey,
    helperText: dirtyFields.includes(field) ? findValidationError(field) : null,
    error: dirtyFields.includes(field) && !!findValidationError(field),
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("Fleet.Settings.NewPassModal.CreatePass")}</DialogTitle>
      {passOfferTypes ? (
        <DialogContent>
          <Grid container justify="space-around">
            <Grid item xs={12} md={9}>
              <FilledSelect
                value={passes?.pass_offer_type_id}
                label={t("Fleet.Settings.NewPassModal.type")}
                onChange={(e) =>
                  handleFormChange(PassOfferConstants.PASS_OFFER_TYPE_ID, e.target.value)
                }
                FormControlProps={{ fullWidth: true, margin: "normal" }}
                onBlur={() =>
                  setDirtyFields([...dirtyFields, PassOfferConstants.PASS_OFFER_TYPE_ID])
                }
                helperText={
                  dirtyFields.includes(PassOfferConstants.PASS_OFFER_TYPE_ID)
                    ? findValidationError(PassOfferConstants.PASS_OFFER_TYPE_ID)
                    : null
                }
                error={
                  dirtyFields.includes(PassOfferConstants.PASS_OFFER_TYPE_ID) &&
                  !!findValidationError(PassOfferConstants.PASS_OFFER_TYPE_ID)
                }
              >
                {passOfferTypes?.map((type) => (
                  <MenuItem value={type.id} key={type.id}>
                    {passOfferTypeStrParser(type)}
                  </MenuItem>
                ))}
              </FilledSelect>
            </Grid>
            <Grid item xs={12} md={9}>
              <FilledTextField
                {...getStandardTextFieldProps("start_date")}
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <FilledTextField
                {...getStandardTextFieldProps("end_date")}
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <FilledTextField
                {...getStandardTextFieldProps("price")}
                mask={activeRatePlan?.currency}
                disabled={
                  passOfferTypes.find(
                    (el) =>
                      el.id === passes.pass_offer_type_id &&
                      el.offer_type === PassOfferConstants.LINKUP,
                  ) !== undefined
                }
              />
            </Grid>
            {submitError && (
              <Grid item xs={12} md={9}>
                <Typography color="error">{submitError}</Typography>
              </Grid>
            )}
          </Grid>
          {loading && <LinearProgress color="secondary" />}
        </DialogContent>
      ) : (
        <DialogContent>
          <Typography>{t("Fleet.Settings.NewPassModal.noPassOfferTypes")}</Typography>
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={handleClose}>{t("Fleet.Settings.NewPassModal.Cancel")}</Button>
        <Button onClick={handleSubmit} disabled={!!invalids.passes.length}>
          {t("Fleet.Settings.NewPassModal.Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewPassOffer;
